import type { ProductInfo } from '@/hooks/analytics/types';
import type { ShownPrice } from '@/utils/promoOffer/createUpsellPurchaseCompletedProps';

/**
 * The purchase-success analytics inputs computed on the payment page (where the
 * redux purchase context is live). For the redirect-required (3DS / some wallet)
 * path, this is stashed in `sessionStorage` before `checkout.confirm()` and
 * replayed on `/checkout/return` after the Stripe redirect resets redux — so the
 * event carries the same money/plan fields the on-page path logs.
 *
 * `moneyEventProps` is the `createSuccessEventProps` output (redux-derived
 * unit_price / currency / local_price / plan / promotion / amp_order_id / …)
 * WITHOUT the subscription-derived details, which are added at fire time from
 * the freshly-polled active subscription.
 */
export type CheckoutSuccessPayload = {
	eventName: string;
	// `${analyticsPrefix} : Pending` baked in alongside `eventName` so the redirect-fallback
	// replay (/checkout/return) fires the pending event under the caller's prefix too — the
	// `/checkout/return` hook instance has no args, so its closure `analyticsPrefix` defaults
	// to 'Subscribe : Purchase : Form'. Symmetric with `eventName` for the Success path.
	pendingEventName: string;
	productInfo: ProductInfo;
	transactionId: string | null;
	moneyEventProps: Record<string, unknown>;
	// Caller attribution props, baked in so they survive the 3DS redirect (the `/checkout/return`
	// hook instance has no closure copy). Merged into the replayed Success event.
	analyticsEventProps?: Record<string, string>;
	// Offer-link params (flavor, variant, pack_class, coupon) for the upsell conversion event.
	// Optional so a payload persisted without it still replays.
	promoParams?: Record<string, string>;
	// The plan price the page showed, for the upsell event when the subscription carries no charge.
	shownPrice?: ShownPrice;
};

const storageKey = (sessionId: string): string => `gro16779-checkout-success:${sessionId}`;

export const persistCheckoutSuccessPayload = (sessionId: string, payload: CheckoutSuccessPayload): void => {
	try {
		window.sessionStorage.setItem(storageKey(sessionId), JSON.stringify(payload));
	} catch {
		// sessionStorage can throw (private mode / quota); the on-page path is
		// unaffected and the redirect path simply skips the replayed event.
	}
};

export const readCheckoutSuccessPayload = (sessionId: string): CheckoutSuccessPayload | null => {
	try {
		const raw = window.sessionStorage.getItem(storageKey(sessionId));
		return raw ? (JSON.parse(raw) as CheckoutSuccessPayload) : null;
	} catch {
		return null;
	}
};

export const clearCheckoutSuccessPayload = (sessionId: string): void => {
	try {
		window.sessionStorage.removeItem(storageKey(sessionId));
	} catch {
		// no-op
	}
};

const processedKey = (sessionId: string): string => `gro16779-checkout-processed:${sessionId}`;

/**
 * True once some run (this tab) has claimed — or finished — this session's terminal
 * `/checkout/return` funnel event (Success OR Pending). Read-only: used both as the
 * fast-path "already fully done, skip polling" check and, implicitly, by
 * `claimCheckoutSessionProcessing` below. Tab-scoped (sessionStorage), so a brand-new
 * tab/window (a second tab, an emailed `session_id` link) is NOT deduped — an
 * acceptable residual, since the fulfillment webhook is the server-side source of
 * truth for fulfillment + revenue.
 */
export const wasCheckoutSessionProcessed = (sessionId: string): boolean => {
	try {
		return window.sessionStorage.getItem(processedKey(sessionId)) === '1';
	} catch {
		return false;
	}
};

/**
 * Atomically claims this session for firing its terminal funnel event: the FIRST
 * caller (per sessionId, per tab) gets `true`; every other caller gets `false` and
 * must skip firing — including a sibling run from a React StrictMode dev remount (or
 * any other overlapping mount) racing the same branch for the same session. The read
 * and the write happen in one synchronous call with no `await` in between, so two
 * racing callers can't both observe "unclaimed" regardless of timing.
 *
 * Call this only AFTER any earlier abortable work in the branch (e.g. an enrichment
 * poll) — claiming before it would strand the session "processed" for an event that
 * never actually fires if that earlier work is cancelled.
 */
export const claimCheckoutSessionProcessing = (sessionId: string): boolean => {
	try {
		if (window.sessionStorage.getItem(processedKey(sessionId)) === '1') {
			return false;
		}
		window.sessionStorage.setItem(processedKey(sessionId), '1');
		return true;
	} catch {
		// sessionStorage can throw (private mode / quota) — degrade to "always claims"
		// (can't dedupe without storage), never a crash.
		return true;
	}
};
